import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import GuestLayout from '@/components/guest/GuestLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RequestStatusBadge from '@/components/guest/RequestStatusBadge.jsx';
import { useSession } from '@/hooks/useSession.js';
import { getRelativeTime } from '@/utils/dateFormatter.js';
import { REQUEST_STATUS } from '@/utils/constants.js';
import { Home, Loader2, CheckCircle2, Clock, AlertCircle, BellRing } from 'lucide-react';
import { guestRequestsApi } from '@/api/guestRequests/guestRequestapi.js';
import { toast } from 'sonner';

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || null;
};

const getApiMessage = (error, fallback = "Something went wrong.") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data?.title ||
    error?.message ||
    fallback
  );
};

const getPagedItems = (response) => {
  const data = getApiData(response);
  if (Array.isArray(data)) return data;

  const nestedData = data?.data || data?.Data;

  return (
    data?.items ||
    data?.Items ||
    data?.records ||
    data?.Records ||
    nestedData?.items ||
    nestedData?.Items ||
    (Array.isArray(nestedData) ? nestedData : [])
  );
};

const normalizeRequest = (item) => {
  const room = item?.room || item?.Room;
  const type = item?.requestTypeName || item?.RequestTypeName || item?.requestType || item?.RequestType;
  const sourceType = item?.sourceType || item?.SourceType || "";
  const sourceId = item?.sourceId || item?.SourceId || null;
  const orderId = item?.orderId || item?.OrderId || (
    ["order", "posorder"].includes(String(sourceType).toLowerCase()) ? sourceId : null
  );

  return {
    id: item?.id || item?.Id,
    sourceType,
    sourceId,
    orderId,
    roomNumber: room?.name || room?.Name || item?.roomNumber || item?.RoomNumber || "—",
    serviceType: item?.title || item?.Title || String(type || "Service request").replace(/([a-z])([A-Z])/g, "$1 $2"),
    quantity: item?.quantity || item?.Quantity || 1,
    status: item?.statusName || item?.StatusName || item?.status || item?.Status || REQUEST_STATUS.PENDING,
    createdAt: item?.createdAtUtc || item?.CreatedAtUtc || item?.createdAt || item?.CreatedAt,
    updatedAt: item?.updatedAtUtc || item?.UpdatedAtUtc || item?.statusChangedAtUtc || item?.StatusChangedAtUtc,
    notes: item?.description || item?.Description || "",
    reason: item?.statusReason || item?.StatusReason || "",
    benchmarkMinutes: item?.benchmarkMinutes || item?.BenchmarkMinutes,
    resolutionMinutes: item?.resolutionMinutes || item?.ResolutionMinutes,
    overdueMinutes: item?.overdueMinutes || item?.OverdueMinutes,
    isOverdue: item?.isOverdue ?? item?.IsOverdue ?? false,
    reminderCount: item?.reminderCount || item?.ReminderCount || 0,
    lastReminderAtUtc: item?.lastReminderAtUtc || item?.LastReminderAtUtc,
    acceptedAtUtc: item?.acceptedAtUtc || item?.AcceptedAtUtc,
    inProgressAtUtc: item?.inProgressAtUtc || item?.InProgressAtUtc,
    completedAtUtc: item?.completedAtUtc || item?.CompletedAtUtc,
  };
};

const RequestStatusPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { session, hotel, room } = useSession();
  const guestSessionId = session?.id || session?.Id || session?.sessionId || session?.guestSessionId;
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    const loadRequest = async () => {
      try {
        let requestData = null;

        try {
          const response = await guestRequestsApi.guestGetById(requestId, {
            guestSessionId,
          });
          requestData = getApiData(response);
        } catch (directError) {
          if (!guestSessionId) throw directError;

          const listResponse = await guestRequestsApi.guestGetBySessionId(guestSessionId, {
            pageSize: 100,
          });
          requestData = getPagedItems(listResponse).find((item) => {
            const id = item?.id || item?.Id;
            return String(id) === String(requestId);
          });
        }

        const normalized = requestData ? normalizeRequest(requestData) : null;
        setRequest(normalized?.id ? normalized : null);
      } catch (error) {
        console.error('Failed to load request:', error);
        setRequest(null);
      } finally {
        setLoading(false);
      }
    };

    loadRequest();
    const interval = setInterval(loadRequest, 10000);
    return () => clearInterval(interval);
  }, [requestId, guestSessionId]);

  const handleReminder = async () => {
    try {
      setReminding(true);
      const response = await guestRequestsApi.remind(request?.id || requestId, {
        note: "Guest asked for a quick follow-up.",
        isInternal: false,
        guestSessionId,
      });
      setRequest(normalizeRequest(getApiData(response)));
      toast.success("Reminder sent to hotel staff");
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to send reminder right now."));
    } finally {
      setReminding(false);
    }
  };

  if (loading) {
    return (
      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
      </GuestLayout>
    );
  }

  if (!request) {
    return (
      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <p className="text-muted-foreground mb-4">Request not found</p>
          <Button onClick={() => navigate('/guest/home')}>Back to home</Button>
        </div>
      </GuestLayout>
    );
  }

  const statusSteps = [
    { status: REQUEST_STATUS.PENDING, icon: Clock, label: 'Request received' },
    { status: REQUEST_STATUS.ACCEPTED, icon: AlertCircle, label: 'Request accepted' },
    { status: REQUEST_STATUS.IN_PROGRESS, icon: Loader2, label: 'In progress' },
    { status: REQUEST_STATUS.COMPLETED, icon: CheckCircle2, label: 'Completed' }
  ];

  const currentStepIndex = statusSteps.findIndex(step => step.status === request.status);

  return (
    <>
      <Helmet>
        <title>{`Request Status - ${hotel?.name || 'The Hotelier'}`}</title>
        <meta name="description" content="Track your service request status" />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-balance">Request status</h1>
            <p className="text-muted-foreground">Track your service request progress</p>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <CardTitle>{request.serviceType}</CardTitle>
                <RequestStatusBadge status={request.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Room</p>
                  <p className="font-medium">{request.roomNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Quantity</p>
                  <p className="font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>{request.quantity}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Requested</p>
                  <p className="text-sm">{getRelativeTime(request.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Last updated</p>
                  <p className="text-sm">{getRelativeTime(request.updatedAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Standard time</p>
                  <p className="text-sm">{request.benchmarkMinutes ? `${request.benchmarkMinutes} min` : "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Time taken</p>
                  <p className="text-sm">{request.resolutionMinutes ? `${request.resolutionMinutes} min` : request.isOverdue ? `${request.overdueMinutes} min overdue` : "In progress"}</p>
                </div>
              </div>

              {request.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm bg-muted p-3 rounded-lg">{request.notes}</p>
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <p className="text-sm font-medium mb-4">Progress timeline</p>
                <div className="space-y-4">
                  {statusSteps.map((step, index) => {
                    const Icon = step.icon;
                    const isCompleted = index <= currentStepIndex;
                    const isCurrent = index === currentStepIndex;

                    return (
                      <div key={step.status} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isCompleted ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        }`}>
                          <Icon className={`w-4 h-4 ${isCurrent && step.status === REQUEST_STATUS.IN_PROGRESS ? 'animate-spin' : ''}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {step.label}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2">
            <Button
              onClick={handleReminder}
              disabled={reminding || [REQUEST_STATUS.COMPLETED, REQUEST_STATUS.CANCELLED, "Rejected"].includes(request.status)}
              className="transition-all duration-200"
            >
              {reminding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />}
              Remind staff
            </Button>
            <Button 
              onClick={() => navigate('/guest/home')} 
              variant="outline"
              className="transition-all duration-200"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to home
            </Button>
          </div>
        </div>
      </GuestLayout>
    </>
  );
};

export default RequestStatusPage;
